<template>
  <div
    class="search-filter-page"
    :class="themeClass"
    :style="searchFilterColorStyles"
  >
    <main class="search-filter-main-wrapper">
      <div class="search-filter-filter-pane">
        <div
          v-show="jobSearchGlobal && isSearchReady"
          class="search-filter-searchbox"
        >
          <IconMagnifyingGlass width="30" height="30" />
          <slot name="search-box"></slot>
        </div>

        <template v-if="jobSearchGlobal && isSearchReady" />
        <SkeletonLoader v-else height="60px" />

        <div
          v-show="jobSearchGlobal && isSearchReady"
          class="search-filter-filters"
        >
          <GlobalUIDropdown
            v-for="filter in activeFilters"
            :key="filter.key"
            :ref="`filter-${filter.key}`"
            full-screen-on-mobile
            :content-class-name="'search-filter-filter-dropdown ' + themeClass"
            :content-styles="searchFilterColorStyles"
            teleport-target="body"
            :style="{ display: filter.preset?.length ? 'none' : null }"
          >
            <template #trigger>
              <FilterButton
                :label="filter.label"
                :filter-key="filter.key"
                :accent-color="jobSearchGlobal.config.general.accentColor.value"
                @click="$emit('filter-clicked', filter.key)"
              />
            </template>

            <template #mobile-close-button>
              <div class="search-filter-mobile-close-button">
                <IconChevronRight />
                {{ jobSearchGlobal.config.filters.backToResultsText }}
              </div>
            </template>

            <template #close-button>
              <div class="search-filter-close-button">
                <IconClose width="24" height="24" />
                <span>
                  {{ jobSearchGlobal.config.filters.backToResultsText }}
                </span>
              </div>
            </template>

            <template #content>
              <slot :name="`filter-${filter.type}`" :filter="filter" />
              <slot name="single-filter-reset" :filter="filter" />
            </template>
          </GlobalUIDropdown>
        </div>

        <template v-if="jobSearchGlobal && isSearchReady" />
        <div v-else class="search-filter-filters">
          <SkeletonLoader />
        </div>
      </div>

      <slot v-if="currentView === 'list'" name="results-loading" />

      <div v-if="showInitialResultsLoading">
        <ResultsLoading :job-search-global="jobSearchGlobal" />
      </div>

      <div v-else>
        <div class="search-filter-results-top-row">
          <div class="search-filter-stats-and-sorting">
            <div
              v-if="jobSearchGlobal.config.map?.showMap"
              class="search-filter-results-view-tablist"
              role="tablist"
              :aria-label="searchTablistAriaLabel"
            >
              <button
                id="list-view-tab"
                :class="currentView === 'list' ? '-selected' : ''"
                type="button"
                role="tab"
                :aria-selected="currentView === 'list'"
                aria-controls="list-view"
                :aria-label="toggleListViewAriaLabel"
                @click="changeTab('list')"
              >
                <IconList />
              </button>
              <button
                id="map-view-tab"
                :class="currentView === 'map' ? '-selected' : ''"
                type="button"
                role="tab"
                :aria-selected="currentView === 'map'"
                aria-controls="map-view"
                :aria-label="toggleMapViewAriaLabel"
                @click="changeTab('map')"
              >
                <IconMap />
              </button>
            </div>

            <slot name="stats"></slot>

            <div v-if="showSorting" class="separator" />

            <div v-if="showSorting" class="sorting-wrapper">
              <p class="sorting-text">
                {{ jobSearchGlobal.config.sorting.text }}
              </p>
              <div class="sorting-select">
                <slot name="sorting-select"></slot>
                <IconChevronDown />
              </div>
            </div>
          </div>
          <div class="search-filter-all-filter-reset">
            <slot name="all-filter-reset" />
          </div>
        </div>

        <div
          v-if="currentView === 'list'"
          id="list-view-tabpanel"
          role="tabpanel"
          aria-labelledby="list-view-tabpanel"
        >
          <slot name="results"></slot>
          <slot name="empty-state"></slot>
        </div>

        <div
          v-if="currentView === 'map'"
          id="map-view-tabpanel"
          role="tabpanel"
          aria-labelledby="map-view-tabpanel"
        >
          <slot name="map-view"></slot>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import SearchStats from "../SearchStats/SearchStats.vue";
import ResultItem from "../ResultItem/ResultItem.vue";
import InfiniteScrollTrigger from "../InfiniteScrollTrigger/InfiniteScrollTrigger.vue";
import IconChevronDown from "../../Icons/IconChevronDown.vue";
import IconMagnifyingGlass from "../../Icons/IconMagnifyingGlass.vue";
import IconPlus from "../../Icons/IconPlus.vue";
import IconChevronRight from "../../Icons/IconChevronRight.vue";
import IconClose from "../../Icons/IconClose.vue";
import IconList from "../../Icons/IconList.vue";
import IconMap from "../../Icons/IconMap.vue";
import GlobalUIDropdown from "../../GlobalUI/GlobalUIDropdown/GlobalUIDropdown.vue";
import FilterButton from "../FilterButton/FilterButton.vue";
import ResultsLoading from "../ResultsLoading/ResultsLoading.vue";
import SkeletonLoader from "../../SkeletonLoader/SkeletonLoader.vue";

import jobSearchGlobal from "../../../globalSettingProps/jobSearchDefaultGlobalProp.js";
import { searchAndFilterComputed } from "../mixins/searchAndFilter.js";

export default {
  name: "SearchFilterPageTemplate",
  components: {
    SkeletonLoader,
    ResultsLoading,
    IconChevronRight,
    IconClose,
    IconMagnifyingGlass,
    IconPlus,
    IconChevronDown,
    IconList,
    IconMap,
    InfiniteScrollTrigger,
    ResultItem,
    SearchStats,
    GlobalUIDropdown,
    FilterButton,
  },
  mixins: [searchAndFilterComputed],
  inject: {
    searchState: {
      from: "$_ais_instantSearchInstance", // This is provided by the AisInstantSearch wrapper component
    },
  },
  provide() {
    return {
      currentRefinements: this.computedFunction(() => this.currentRefinements),
      initialHits: this.computedFunction(() => this.initialHits),
    };
  },
  props: {
    jobSearchGlobal: {
      type: Object,
      default() {
        return jobSearchGlobal;
      },
    },
    computedFunction: {
      type: Function,
      required: true,
    },
    searchTablistAriaLabel: {
      type: String,
      required: true,
      default: "Search View Toggle Buttons",
    },
    toggleMapViewAriaLabel: {
      type: String,
      required: true,
      default: "Toggle Map View",
    },
    toggleListViewAriaLabel: {
      type: String,
      required: true,
      default: "Toggle List View",
    },
  },
  emits: ["filter-clicked", "view-changed"],
  data() {
    return {
      currentRefinements: [],
      helper: null,
      isInitialized: false,
      showSorting: false, // see https://talentsconnect.atlassian.net/browse/CROS-62
      initialHits: null,
      isSearchReady: false,
      currentView: "list",
    };
  },
  computed: {
    activeFilters() {
      return this.jobSearchGlobal.config.filters.options.filter(
        (filter) => filter.active === true,
      );
    },
    showInitialResultsLoading() {
      // Show the ResultsLoading component if during initial loading when initialHits is still null
      if (this.initialHits === null) return true;

      // Do not show it if we don't get any results (handled by empty state)
      if (this.isSearchReady && !this.initialHits) return false;

      // false as the default
      return false;
    },
    themeClass() {
      const theme = this.jobSearchGlobal.config.general.theme.value || "";

      return "theme-" + theme;
    },
  },
  mounted() {
    this.initializeHelper();
    this.setSearchIsReady();
  },
  beforeUnmount() {
    // Clean up the listener when the component is unmounted
    this.removeChangeListener();
  },
  methods: {
    // Method to update the refinements from the helper state
    updateRefinements() {
      const tempCurrentRefinements = [];

      if (this.helper && this.helper.state) {
        // Check disjunctiveFacetsRefinements and add those with value to tempCurrentRefinements
        Object.entries(this.helper.state.disjunctiveFacetsRefinements).forEach(
          ([key, value]) => {
            if (value.length > 0) {
              tempCurrentRefinements.push({
                key,
                value,
              });
            }
          },
        );

        // Check numericRefinements and add those with value to tempCurrentRefinements
        Object.entries(this.helper.state.numericRefinements).forEach(
          ([key, value]) => {
            // Check if the value object has any keys, and at least one of those keys has a valid nested value
            const hasNestedValidValue = Object.values(value).some((nestedObj) =>
              Object.values(nestedObj).some(
                (nestedValue) =>
                  nestedValue !== undefined &&
                  nestedValue !== null &&
                  nestedValue !== "",
              ),
            );

            if (hasNestedValidValue) {
              tempCurrentRefinements.push({
                key,
                value: ["active"], // Set artificial value here to match the structure of the disjunctiveFacetsRefinements
              });
            }
          },
        );

        // Update currentRefinements data
        this.currentRefinements = tempCurrentRefinements;
      }
    },
    // Method to read the initial hits from the helper
    getInitialHits() {
      const checkLastResults = () => {
        if (this.helper && this.helper.lastResults) {
          const rawResults = this.helper.lastResults._rawResults;

          // Initial number of hits is always the last index
          this.initialHits = rawResults[rawResults.length - 1].nbHits;
        } else {
          // Retry after 50ms if lastResults is not ready
          setTimeout(checkLastResults, 50);
        }
      };

      checkLastResults();
    },
    // Method to add the event listener safely
    addChangeListener() {
      if (this.helper && !this.isInitialized) {
        this.helper.on("change", this.updateRefinements);
        this.isInitialized = true;
      }
    },
    // Method to remove the event listener safely
    removeChangeListener() {
      if (this.helper && this.isInitialized) {
        this.helper.removeListener("change", this.updateRefinements);
        this.isInitialized = false;
      }
    },
    // Method to initialize helper with a polling mechanism
    initializeHelper() {
      const checkHelper = () => {
        this.helper = this.searchState?.mainIndex?.getHelper();
        if (this.helper) {
          this.addChangeListener();
          this.updateRefinements();
          this.getInitialHits();
        } else {
          // Retry after 50ms if helper is not ready
          setTimeout(checkHelper, 50);
        }
      };

      // Start the helper check
      checkHelper();
    },
    setSearchIsReady() {
      // Use artificial delay to wait for instantsearch widgets to be ready
      setTimeout(() => {
        this.isSearchReady = true;
      }, 350);
    },
    changeTab(targetTab) {
      this.currentView = targetTab;

      // Emit events based on the selected tab
      this.$emit("view-changed", targetTab);
    },
  },
};
</script>
<style lang="scss" scoped>
@import "../../../assets/scss/search-filter/variables";
</style>
